/**
 * HealthSyncButton — one-tap "Sync from Apple Health / Health Connect".
 * Renders ONLY inside the installed native app (hidden in the browser PWA and
 * on iPad, which has no HealthKit). Pulls today's steps + recent weight.
 */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { HeartPulse, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { isHealthAvailable, syncHealthData } from "@/lib/healthSync";

export default function HealthSyncButton({ stepEventSlug, onSynced }) {
  const [available, setAvailable] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    isHealthAvailable().then((r) => { if (live) setAvailable(!!r.available); });
    return () => { live = false; };
  }, []);

  if (!available) return null;

  const run = async () => {
    setBusy(true);
    try {
      const r = await syncHealthData(stepEventSlug);
      if (r.error) { toast.error(r.error); return; }
      const parts = [];
      if (r.steps != null) parts.push(`${r.steps.toLocaleString()} steps today`);
      if (r.weightImported != null) parts.push(`${r.weightImported} weigh-in${r.weightImported === 1 ? "" : "s"}`);
      toast.success(parts.length ? `Synced ${parts.join(" · ")}` : "Health data is up to date");
      onSynced?.(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Health sync failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button
      onClick={run}
      disabled={busy}
      variant="outline"
      className="w-full rounded-xl border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-sm py-5"
      data-testid="health-sync-button"
    >
      {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <HeartPulse className="w-4 h-4 mr-2" />}
      {busy ? "Syncing…" : "Sync from Apple Health / Health Connect"}
    </Button>
  );
}
